/**
 * Defines the versioned public contract for opening a complaint case.
 */
import { z } from "zod";
import { PublicCaseClassificationSchema, PublicCaseClassificationSelectionSchema, PublicCaseReasonCatalogSchema, } from "./CaseReasonContract.js";
const optionalEmailSchema = z
    .union([z.literal(""), z.email().max(254), z.null()])
    .transform((value) => (value === "" ? null : value));
const decimalMoneySchema = z
    .string()
    .regex(/^(?:0|[1-9]\d{0,11})(?:\.\d{1,2})?$/, "Use a positive decimal with at most 2 decimals.")
    .refine((value) => Number(value) > 0, "The amount must be greater than zero.");
/**
 * Stable public document codes backed by a database catalog.
 */
export const PublicDocumentTypeSchema = z.enum(["DNI", "FOREIGN_RESIDENT_CARD", "PASSPORT", "RUC"]);
const dniDocumentNumberPattern = /^\d{8}$/;
const rucDocumentNumberPattern = /^\d{11}$/;
const alphanumericDocumentNumberPattern = /^[A-Za-z0-9]{1,32}$/;
/**
 * A DNI/RUC has a fixed digit count; other document types stay a loose
 * alphanumeric value because their format is not standardized nationally.
 */
function IsValidDocumentNumber(documentType, documentNumber) {
    if (documentType === "DNI") {
        return dniDocumentNumberPattern.test(documentNumber);
    }
    if (documentType === "RUC") {
        return rucDocumentNumberPattern.test(documentNumber);
    }
    return alphanumericDocumentNumberPattern.test(documentNumber);
}
function DocumentNumberErrorMessage(documentType) {
    if (documentType === "DNI") {
        return "DNI must contain exactly 8 digits.";
    }
    if (documentType === "RUC") {
        return "RUC must contain exactly 11 digits.";
    }
    return "Use up to 32 letters or digits.";
}
function CheckDocumentNumber(value, context) {
    if (!IsValidDocumentNumber(value.documentType, value.documentNumber)) {
        context.addIssue({
            code: "custom",
            message: DocumentNumberErrorMessage(value.documentType),
            path: ["documentNumber"],
        });
    }
}
/**
 * Stable requested response channels backed by a database catalog.
 */
export const PublicResponseChannelSchema = z.enum(["EMAIL", "LETTER"]);
/**
 * Stable contracted-item classifications backed by a database catalog.
 */
export const PublicContractedItemTypeSchema = z.enum(["PRODUCT", "SERVICE", "PRODUCT_AND_SERVICE"]);
/**
 * Stable complaint classifications backed by a database catalog.
 */
export const PublicCaseTypeSchema = z.enum(["CLAIM", "COMPLAINT"]);
/**
 * Supported currencies for a claimed amount.
 */
export const PublicCurrencySchema = z.enum(["PEN", "USD"]);
/**
 * Stable representative relationships backed by a database catalog.
 */
export const PublicRepresentativeRelationshipSchema = z.enum([
    "PARENT",
    "LEGAL_GUARDIAN",
    "OTHER_REPRESENTATIVE",
]);
/**
 * Consumer data confirmed in the public form.
 */
export const PublicConsumerSchema = z
    .object({
    address: z.string().trim().min(5).max(500),
    documentNumber: z.string().trim().min(1).max(32),
    documentType: PublicDocumentTypeSchema,
    email: optionalEmailSchema,
    fullName: z.string().trim().min(2).max(200),
    isMinor: z.boolean(),
    phoneCountryCode: z
        .string()
        .trim()
        .regex(/^\+\d{1,3}$/),
    phoneNumber: z
        .string()
        .trim()
        .regex(/^\d{6,15}$/),
    requestedResponseChannel: PublicResponseChannelSchema,
})
    .strict()
    .superRefine(CheckDocumentNumber);
/**
 * Representative data required only when the consumer is a minor.
 */
export const PublicRepresentativeSchema = z
    .object({
    address: z.string().trim().min(5).max(500),
    documentNumber: z.string().trim().min(1).max(32),
    documentType: PublicDocumentTypeSchema,
    email: optionalEmailSchema,
    fullName: z.string().trim().min(2).max(200),
    phoneCountryCode: z
        .string()
        .trim()
        .regex(/^\+\d{1,3}$/),
    phoneNumber: z
        .string()
        .trim()
        .regex(/^\d{6,15}$/),
    relationship: PublicRepresentativeRelationshipSchema,
})
    .strict()
    .superRefine(CheckDocumentNumber);
/**
 * Product or service data and optional exact decimal amount.
 */
export const PublicContractedItemSchema = z
    .object({
    claimedAmount: z
        .object({
        currency: PublicCurrencySchema,
        value: decimalMoneySchema,
    })
        .nullable(),
    description: z.string().trim().min(5).max(1_000),
    type: PublicContractedItemTypeSchema,
})
    .strict();
/**
 * Immutable complaint content confirmed by the consumer.
 */
export const PublicClaimSchema = z
    .object({
    detail: z.string().trim().min(10).max(5_000),
    requestedAction: z.string().trim().min(5).max(3_000),
    type: PublicCaseTypeSchema,
})
    .strict();
/**
 * Versioned virtual-conformity evidence sent by the public form.
 */
export const PublicConfirmationSchema = z
    .object({
    accepted: z.boolean().refine((value) => value, "The declaration must be accepted."),
    formVersion: z.string().trim().min(1).max(64).optional(),
    privacyNoticeVersion: z.string().trim().min(1).max(128).optional(),
})
    .strict();
/**
 * Request body for public case creation. Company and branch are deliberately absent.
 */
export const CreatePublicCaseRequestSchema = z
    .object({
    claim: PublicClaimSchema,
    /**
     * Consumer-supplied classification. Absent means the company either has no
     * catalog or does not require one; the server, not this schema, decides
     * whether that is acceptable, because only it knows the company policy.
     */
    classification: PublicCaseClassificationSelectionSchema.optional(),
    confirmation: PublicConfirmationSchema,
    consumer: PublicConsumerSchema,
    contractedItem: PublicContractedItemSchema,
    incidentDate: z.iso.date(),
    representative: PublicRepresentativeSchema.nullable(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.consumer.isMinor && value.representative === null) {
        context.addIssue({
            code: "custom",
            message: "Representative data is required for a minor.",
            path: ["representative"],
        });
    }
    if (!value.consumer.isMinor && value.representative !== null) {
        context.addIssue({
            code: "custom",
            message: "Representative data is only accepted for a minor.",
            path: ["representative"],
        });
    }
    if (value.consumer.requestedResponseChannel === "EMAIL" && value.consumer.email === null) {
        context.addIssue({
            code: "custom",
            message: "Email is required for email responses.",
            path: ["consumer", "email"],
        });
    }
});
/**
 * Header contract for mutation replay protection.
 */
export const PublicCaseIdempotencyKeySchema = z
    .string()
    .trim()
    .min(16)
    .max(128)
    .regex(/^[A-Za-z0-9._~-]+$/);
/**
 * Stable public URL slug. It identifies a server-resolved origin, never a database ID.
 */
export const PublicBookSlugSchema = z
    .string()
    .min(1)
    .max(120)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);
export const PublicCompanyOriginParametersSchema = z
    .object({ companySlug: PublicBookSlugSchema })
    .strict();
export const PublicBranchOriginParametersSchema = z
    .object({
    branchSlug: PublicBookSlugSchema,
    companySlug: PublicBookSlugSchema,
})
    .strict();
export const PublicCaseHeadersSchema = z
    .object({ "idempotency-key": PublicCaseIdempotencyKeySchema.optional() })
    .strict();
const publicProviderSchema = z
    .object({
    address: z.string(),
    dataControllerName: z.string(),
    legalName: z.string(),
    supportChannel: z.string(),
    taxId: z.string(),
    tradeName: z.string().nullable(),
})
    .strict();
const publicBranchSchema = z
    .object({
    address: z.string().nullable(),
    bookCode: z.string(),
    name: z.string(),
})
    .strict()
    .nullable();
/**
 * Public visual identity of the provider. Absent means the default palette.
 */
const publicBrandingSchema = z
    .object({
    deepColor: z.string().regex(/^#[0-9A-F]{6}$/u),
    faviconUrl: z.string().nullable(),
    logoUrl: z.string().nullable(),
    onPrimaryColor: z.string().regex(/^#[0-9A-F]{6}$/u),
    primaryColor: z.string().regex(/^#[0-9A-F]{6}$/u),
})
    .strict()
    .nullable();
/**
 * Safe server-resolved context displayed before the consumer submits.
 */
export const PublicBookOriginResponseSchema = z
    .object({
    branch: publicBranchSchema,
    branding: publicBrandingSchema,
    /**
     * Active classification catalog for this company. An empty list means the
     * form renders no classification section at all.
     */
    classification: PublicCaseReasonCatalogSchema,
    form: z.object({
        confirmationText: z.string(),
        privacyNotice: z.string(),
        privacyNoticeVersion: z.string(),
        privacyPolicyUrl: z.string().nullable(),
        version: z.string(),
    }),
    provider: publicProviderSchema,
    timezone: z.string(),
})
    .strict();
/**
 * Immutable receipt returned only after the transaction commits.
 */
export const PublicCaseReceiptSchema = z
    .object({
    bookNumber: z.string().nullable(),
    claim: PublicClaimSchema,
    /**
     * Classification as it stood when the receipt was sealed. A later internal
     * reclassification does not rewrite the artifact handed to the consumer.
     */
    classification: PublicCaseClassificationSchema.nullable(),
    confirmation: z
        .object({
        acceptedAt: z.iso.datetime({ offset: true }),
        complianceProfileVersion: z.string(),
        declaration: z.string(),
        formVersion: z.string(),
        privacyNotice: z.string(),
        privacyNoticeVersion: z.string(),
    })
        .strict(),
    consumer: PublicConsumerSchema,
    contractedItem: PublicContractedItemSchema,
    incidentDate: z.iso.date(),
    provider: publicProviderSchema.extend({
        branch: publicBranchSchema,
    }),
    publicId: z.string().regex(/^\d+$/),
    receivedAt: z.iso.datetime({ offset: true }),
    representative: PublicRepresentativeSchema.nullable(),
})
    .strict();
/**
 * Public creation response. Internal UUIDs never cross this boundary.
 */
export const CreatePublicCaseResponseSchema = z
    .object({
    accessToken: z.string().startsWith("lrc_").min(48),
    publicId: z.string().regex(/^\d+$/),
    receipt: PublicCaseReceiptSchema,
    wasReplay: z.boolean(),
})
    .strict();
/**
 * Versioned internal event name written to the transactional outbox.
 */
export const CaseCreatedEventName = "cases.case-created.v1";
/**
 * Minimal non-PII payload consumed by the future outbox publisher.
 */
export const CaseCreatedEventPayloadSchema = z
    .object({
    branchId: z.uuid().nullable(),
    caseId: z.uuid(),
    companyId: z.uuid(),
    occurredAt: z.iso.datetime({ offset: true }),
    publicId: z.string().regex(/^\d+$/),
})
    .strict();
//# sourceMappingURL=PublicCaseContract.js.map